import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Modal,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridPaginationModel,
} from '@mui/x-data-grid';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';

import editIcon from '../../assets/edit-icon.svg';
import AlertTriangle from '../../assets/AlertTriangle.svg';

import {
  pageBoxSx,
  headerRowSx,
  leftTextSx,
  createButtonWrapperSx,
  controlsRowSx,
  allSelectCombinedSx,
  nodeDetailsSx,
  addAccountSX,
} from '../leStations/LEStations.styles';
import {
  activeTagSx,
  inactiveTagSx,
  activeDotColor,
  inactiveDotColor,
  nodeNameSx,
  nodeCitySx,
  powerIconSx,
} from '../node/operatingNodes.styles';
import {
  modalContainerSx as confirmModalContainerSx,
  modalHeaderSx as confirmModalHeaderSx,
  modalTitleSx as confirmModalTitleSx,
  modalCloseIconSx,
  modalContentSx as confirmModalContentSx,
  modalWarningRowSx,
  modalCancelButtonSx,
  modalDeactivateButtonSx,
  modalBodyTextSx,
} from '../node/createNode.styles';

import {
  tabsRowSx,
  tabButtonSx,
  modalContainerSx,
  modalHeaderSx,
  modalTitleSx,
  modalBodySx,
  modalFooterSx,
  fieldLabelSx,
  inputSx,
  cancelButtonSx,
  saveButtonSx,
  formErrorSx,
} from './geography.styles';

import {
  CountryItem,
  StateItem,
  CityItem,
  DistrictItem,
  getCountriesAll,
  getStatesAll,
  getCitiesAll,
  getDistrictsAll,
  createCountry,
  updateCountry,
  setCountryActive,
  createState,
  updateState,
  setStateActive,
  createCity,
  updateCity,
  setCityActive,
  createDistrict,
  updateDistrict,
  setDistrictActive,
} from '../../services/geographyService';

type TabKey = 'country' | 'state' | 'city' | 'district';

type Row = {
  id: number;
  name: string;
  country_id?: number;
  state_id?: number;
  countryName?: string;
  stateName?: string;
  active: boolean;
};

const TAB_DEFS: { key: TabKey; label: string }[] = [
  { key: 'country', label: 'Country' },
  { key: 'state', label: 'State' },
  { key: 'city', label: 'City' },
  { key: 'district', label: 'District' },
];

const Geography: React.FC<{ notification?: { show: boolean; message?: string; severity?: 'success' | 'error' } }> = ({ notification }) => {
  const [activeTab, setActiveTab] = useState<TabKey>('country');
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<CountryItem[]>([]);
  const [states, setStates] = useState<StateItem[]>([]);
  const [cities, setCities] = useState<CityItem[]>([]);
  const [districts, setDistricts] = useState<DistrictItem[]>([]);

  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  // form drawer state
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formName, setFormName] = useState('');
  const [formCountryId, setFormCountryId] = useState<number | ''>('');
  const [formStateId, setFormStateId] = useState<number | ''>('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // confirm modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<{ row: Row; nextActive: boolean } | null>(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, s, ci, d] = await Promise.all([
        getCountriesAll().catch(() => [] as CountryItem[]),
        getStatesAll().catch(() => [] as StateItem[]),
        getCitiesAll().catch(() => [] as CityItem[]),
        getDistrictsAll().catch(() => [] as DistrictItem[]),
      ]);
      setCountries(c);
      setStates(s);
      setCities(ci);
      setDistricts(d);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  // map helpers
  const countryNameById = useMemo(() => {
    const map = new Map<number, string>();
    countries.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [countries]);

  const stateNameById = useMemo(() => {
    const map = new Map<number, string>();
    states.forEach((s) => map.set(s.id, s.name));
    return map;
  }, [states]);

  const rows: Row[] = useMemo(() => {
    if (activeTab === 'country') {
      return countries.map((c) => ({ id: c.id, name: c.name, active: c.active }));
    }
    if (activeTab === 'state') {
      return states.map((s) => ({
        id: s.id,
        name: s.name,
        country_id: s.country_id,
        countryName: countryNameById.get(s.country_id) ?? '—',
        active: s.active,
      }));
    }
    if (activeTab === 'city') {
      return cities.map((c) => ({
        id: c.id,
        name: c.name,
        country_id: c.country_id,
        state_id: c.state_id,
        countryName: countryNameById.get(c.country_id) ?? '—',
        stateName: stateNameById.get(c.state_id) ?? '—',
        active: c.active,
      }));
    }
    return districts.map((d) => ({
      id: d.id,
      name: d.name,
      country_id: d.country_id,
      state_id: d.state_id,
      countryName: countryNameById.get(d.country_id) ?? '—',
      stateName: stateNameById.get(d.state_id) ?? '—',
      active: d.active,
    }));
  }, [activeTab, countries, states, cities, districts, countryNameById, stateNameById]);

  const filteredRows = useMemo(() => {
    if (!statusFilter) return rows;
    return rows.filter((r) => (statusFilter === 'Active' ? r.active : !r.active));
  }, [rows, statusFilter]);

  // reset pagination on tab change
  useEffect(() => {
    setPaginationModel((m) => ({ ...m, page: 0 }));
  }, [activeTab, statusFilter]);

  const openCreate = () => {
    setFormMode('create');
    setEditingId(null);
    setFormName('');
    setFormCountryId('');
    setFormStateId('');
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (row: Row) => {
    setFormMode('edit');
    setEditingId(row.id);
    setFormName(row.name);
    setFormCountryId(row.country_id ?? '');
    setFormStateId(row.state_id ?? '');
    setFormError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
  };

  const handleSave = async () => {
    setFormError(null);
    const name = formName.trim();
    if (!name) {
      setFormError('Name is required');
      return;
    }
    if (activeTab !== 'country' && !formCountryId) {
      setFormError('Country is required');
      return;
    }
    if ((activeTab === 'city' || activeTab === 'district') && !formStateId) {
      setFormError('State is required');
      return;
    }

    setSaving(true);
    try {
      if (activeTab === 'country') {
        if (formMode === 'create') {
          const resp = await createCountry({ name });
          window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? 'Country created' } }));
        } else if (editingId != null) {
          const resp = await updateCountry(editingId, { name });
          window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? 'Country updated' } }));
        }
      } else if (activeTab === 'state') {
        const payload: any = { name, country_id: Number(formCountryId) };
        if (formMode === 'create') {
          const resp = await createState(payload);
          window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? 'State created' } }));
        } else if (editingId != null) {
          const resp = await updateState(editingId, payload);
          window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? 'State updated' } }));
        }
      } else if (activeTab === 'city') {
        const payload: any = { name, country_id: Number(formCountryId), state_id: Number(formStateId) };
        if (formMode === 'create') {
          const resp = await createCity(payload);
          window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? 'City created' } }));
        } else if (editingId != null) {
          const resp = await updateCity(editingId, payload);
          window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? 'City updated' } }));
        }
      } else if (activeTab === 'district') {
        const payload: any = { name, country_id: Number(formCountryId), state_id: Number(formStateId) };
        if (formMode === 'create') {
          const resp = await createDistrict(payload);
          window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? 'District created' } }));
        } else if (editingId != null) {
          const resp = await updateDistrict(editingId, payload);
          window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? 'District updated' } }));
        }
      }
      setFormOpen(false);
      await loadAll();
    } catch (err: any) {
      setFormError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const requestToggleActive = (row: Row) => {
    const nextActive = !row.active;
    if (!nextActive) {
      setConfirmTarget({ row, nextActive });
      setConfirmOpen(true);
      return;
    }
    // reactivating is a one-click action
    void doToggleActive(row, true);
  };

  const doToggleActive = async (row: Row, nextActive: boolean) => {
    try {
      let resp: any;
      if (activeTab === 'country') resp = await setCountryActive(row.id, nextActive);
      else if (activeTab === 'state') resp = await setStateActive(row.id, nextActive);
      else if (activeTab === 'city') resp = await setCityActive(row.id, nextActive);
      else resp = await setDistrictActive(row.id, nextActive);
      window.dispatchEvent(
        new CustomEvent('wualt:notification', {
          detail: {
            message: resp?.message ?? `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} ${nextActive ? 'activated' : 'deactivated'}`,
          },
        }),
      );
      await loadAll();
    } catch (err: any) {
      window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? 'Failed to update status' } }));
    } finally {
      setConfirmOpen(false);
      setConfirmTarget(null);
    }
  };

  // states available for the currently selected country in the form
  const statesForSelectedCountry = useMemo(() => {
    if (!formCountryId) return [] as StateItem[];
    return states.filter((s) => s.country_id === Number(formCountryId));
  }, [states, formCountryId]);

  // reset state selection if country changes and the selected state is not under that country
  useEffect(() => {
    if (!formCountryId) {
      if (formStateId !== '') setFormStateId('');
      return;
    }
    if (formStateId && !states.find((s) => s.id === Number(formStateId) && s.country_id === Number(formCountryId))) {
      setFormStateId('');
    }
  }, [formCountryId, states]);

  const tabLabel = TAB_DEFS.find((t) => t.key === activeTab)?.label ?? 'Item';

  const columns = useMemo<GridColDef[]>(() => {
    const base: GridColDef[] = [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 220,
        sortable: false,
        renderCell: (p: GridRenderCellParams) => <Typography sx={nodeNameSx}>{p.row.name}</Typography>,
      },
    ];

    if (activeTab === 'state' || activeTab === 'city' || activeTab === 'district') {
      base.push({
        field: 'countryName',
        headerName: 'Country',
        flex: 0.8,
        minWidth: 140,
        sortable: false,
        renderCell: (p: GridRenderCellParams) => <Typography sx={nodeCitySx as any}>{p.row.countryName}</Typography>,
      });
    }
    if (activeTab === 'city' || activeTab === 'district') {
      base.push({
        field: 'stateName',
        headerName: 'State',
        flex: 0.8,
        minWidth: 140,
        sortable: false,
        renderCell: (p: GridRenderCellParams) => <Typography sx={nodeCitySx as any}>{p.row.stateName}</Typography>,
      });
    }

    base.push({
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        p.row.active ? (
          <Box component="span" sx={activeTagSx}>
            <Box sx={{ position: 'absolute', width: 6, height: 6, borderRadius: '9999px', bgcolor: activeDotColor, top: '9.25px', left: '10px' }} />
            <Box component="span" sx={{ pl: '18px' }}>Active</Box>
          </Box>
        ) : (
          <Box component="span" sx={inactiveTagSx}>
            <Box sx={{ position: 'absolute', width: 6, height: 6, borderRadius: '9999px', bgcolor: inactiveDotColor, top: '9.25px', left: '10px' }} />
            <Box component="span" sx={{ pl: '18px' }}>Inactive</Box>
          </Box>
        )
      ),
    });

    base.push({
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      minWidth: 130,
      sortable: false,
      renderCell: (p: GridRenderCellParams) => {
        const row: Row = p.row as Row;
        const isInactive = !row.active;
        const iconColor = '#6B7280';
        return (
          <Box>
            {/* KAN-129: when inactive only the activate/deactivate toggle stays enabled. */}
            <IconButton size="small" disabled={isInactive} onClick={() => { if (!isInactive) openEdit(row); }}>
              <Box component="img" src={editIcon} alt="Edit" sx={{ width: '14px', height: '14px', opacity: isInactive ? 0.4 : 1 }} />
            </IconButton>
            <IconButton size="small" onClick={() => requestToggleActive(row)} title={isInactive ? 'Activate' : 'Deactivate'}>
              <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <PowerSettingsNewIcon sx={{ color: iconColor, ...powerIconSx }} />
                {isInactive && (
                  <Box sx={{ position: 'absolute', width: '22px', height: '2px', bgcolor: iconColor, transform: 'rotate(45deg)' }} />
                )}
              </Box>
            </IconButton>
          </Box>
        );
      },
    });

    return base;
  }, [activeTab]);

  return (
    <Box sx={pageBoxSx}>
      <Box mb={2}>
        <Box sx={headerRowSx}>
          <Box sx={leftTextSx}>
            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 700, fontSize: '20px', lineHeight: '30px' }}>Geography</Typography>
            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '19.5px', color: '#6B7280' }}>
              Manage countries, states, districts and cities used across the platform.
            </Typography>
          </Box>
          <Box sx={{ ...createButtonWrapperSx, position: 'relative' }}>
            <Button variant="outlined" sx={addAccountSX} onClick={openCreate}>+ Add {tabLabel}</Button>
            {notification && notification.show && (
              (() => {
                const isError = notification.severity === 'error';
                return (
                  <Box sx={{ position: 'absolute', bottom: 'calc(0%)', right: 0, width: '356px', height: '54px', borderRadius: '8px', backgroundColor: isError ? '#FEF2F2' : '#ECFDF3', border: `1px solid ${isError ? '#FCA5A5' : '#A9DEBF'}`, display: 'flex', alignItems: 'center', px: 2 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: isError ? '#B91C1C' : '#008A2E', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                      {isError ? (
                        <svg width="13" height="13" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7M7 1L1 7" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" /></svg>
                      ) : (
                        <svg width="13" height="13" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 3L3 5L7 1" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      )}
                    </Box>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px' }}>{notification.message}</Typography>
                  </Box>
                );
              })()
            )}
          </Box>
        </Box>

        {/* tabs */}
        <Box sx={tabsRowSx}>
          {TAB_DEFS.map((t) => (
            <Button
              key={t.key}
              disableRipple
              sx={tabButtonSx(activeTab === t.key)}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </Button>
          ))}
        </Box>

        {/* filters */}
        <Box sx={controlsRowSx}>
          <Autocomplete
            size="small"
            sx={{ ...allSelectCombinedSx, minWidth: 160 }}
            options={['Active', 'Inactive']}
            value={statusFilter}
            onChange={(_e, val) => setStatusFilter(val)}
            renderInput={(params) => <TextField {...params} placeholder="All Statuses" />}
            clearOnEscape
          />
        </Box>
      </Box>

      <Paper sx={nodeDetailsSx}>
        <div ref={gridContainerRef} style={{ height: '100%', width: '100%' }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            columnHeaderHeight={30}
            rowHeight={45}
            pagination
            pageSizeOptions={[5, 10, 25, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => setPaginationModel(model)}
            loading={loading}
            density="comfortable"
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-root': { position: 'relative' },
              '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F9FAFB !important', borderBottom: '1px solid rgba(0,0,0,0.06)', minHeight: '40px !important', maxHeight: '40px !important', position: 'sticky', top: 0, zIndex: 2 },
              '& .MuiDataGrid-virtualScroller': { maxHeight: 'calc(100vh - 320px)', overflow: 'auto', paddingBottom: '56px' },
              '& .MuiDataGrid-footerContainer': { position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 4, background: '#FFFFFF', borderTop: '1px solid rgba(0,0,0,0.06)' },
              '& .MuiDataGrid-columnHeader': { backgroundColor: '#F9FAFB !important', minHeight: '40px !important', maxHeight: '40px !important', display: 'flex', alignItems: 'center', borderRight: 'none' },
              '& .MuiDataGrid-columnHeaderTitle': { fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: '11px', lineHeight: '16.5px', letterSpacing: '0.06px' },
              '& .MuiDataGrid-columnSeparator': { display: 'none' },
              '& .MuiDataGrid-cell': { borderRight: 'none' },
              '& .MuiDataGrid-cellContent, & .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
            }}
          />
        </div>
      </Paper>

      {/* Add/Edit modal */}
      <Modal open={formOpen} onClose={closeForm}>
        <Box sx={modalContainerSx}>
          <Box sx={modalHeaderSx}>
            <Typography sx={modalTitleSx}>
              {formMode === 'create' ? `Add ${tabLabel}` : `Edit ${tabLabel}`}
            </Typography>
            <IconButton size="small" onClick={closeForm}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </IconButton>
          </Box>

          <Box sx={modalBodySx}>
            {/* Country dropdown (state/city/district) */}
            {(activeTab === 'state' || activeTab === 'city' || activeTab === 'district') && (
              <Box>
                <Typography sx={fieldLabelSx}>Country</Typography>
                <Autocomplete
                  size="small"
                  options={countries.filter((c) => c.active || c.id === Number(formCountryId))}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={countries.find((c) => c.id === Number(formCountryId)) ?? null}
                  onChange={(_e, val) => setFormCountryId(val ? val.id : '')}
                  renderInput={(params) => <TextField {...params} placeholder="Select country" sx={inputSx} />}
                  clearOnEscape
                />
              </Box>
            )}

            {/* State dropdown (city/district) */}
            {(activeTab === 'city' || activeTab === 'district') && (
              <Box>
                <Typography sx={fieldLabelSx}>State</Typography>
                <Autocomplete
                  size="small"
                  options={statesForSelectedCountry.filter((s) => s.active || s.id === Number(formStateId))}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={statesForSelectedCountry.find((s) => s.id === Number(formStateId)) ?? null}
                  onChange={(_e, val) => setFormStateId(val ? val.id : '')}
                  disabled={!formCountryId}
                  renderInput={(params) => <TextField {...params} placeholder={formCountryId ? 'Select state' : 'Select a country first'} sx={inputSx} />}
                  clearOnEscape
                />
              </Box>
            )}

            {/* Name */}
            <Box>
              <Typography sx={fieldLabelSx}>{tabLabel} name</Typography>
              <TextField
                fullWidth
                size="small"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder={`Enter ${tabLabel.toLowerCase()} name`}
                sx={inputSx}
              />
            </Box>

            {formError && <Typography sx={formErrorSx}>{formError}</Typography>}
          </Box>

          <Box sx={modalFooterSx}>
            <Button sx={cancelButtonSx} onClick={closeForm} disabled={saving}>Cancel</Button>
            <Button sx={saveButtonSx} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : formMode === 'create' ? 'Create' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Confirm activate/deactivate modal — KAN-129 */}
      <Modal open={confirmOpen} onClose={() => { setConfirmOpen(false); setConfirmTarget(null); }}>
        <Box sx={confirmModalContainerSx}>
          <Box sx={confirmModalHeaderSx}>
            <Typography sx={confirmModalTitleSx}>{confirmTarget?.nextActive ? 'Activate' : 'Deactivate'} {tabLabel.toLowerCase()}?</Typography>
            <IconButton size="small" onClick={() => { setConfirmOpen(false); setConfirmTarget(null); }} sx={modalCloseIconSx}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </IconButton>
          </Box>
          <Box sx={confirmModalContentSx}>
            <Box sx={modalWarningRowSx}>
              <Box sx={{ width: '80px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: '#FEF2F2', p: 0 }}>
                <Box component="img" src={AlertTriangle} sx={{ width: '16px', height: '16px' }} />
              </Box>
              <Box>
                <Typography sx={modalBodyTextSx}>
                  {confirmTarget?.nextActive
                    ? `Activating this ${tabLabel.toLowerCase()} will make it available again in selection lists.`
                    : `Deactivating this ${tabLabel.toLowerCase()} will hide it from selection lists. You can reactivate it later.`}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button sx={modalCancelButtonSx} onClick={() => { setConfirmOpen(false); setConfirmTarget(null); }}>Cancel</Button>
              <Button sx={modalDeactivateButtonSx} onClick={() => { if (confirmTarget) doToggleActive(confirmTarget.row, confirmTarget.nextActive); }}>{confirmTarget?.nextActive ? 'Activate' : 'Deactivate'}</Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Geography;
